import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FeedbackContext } from '@cvt/contexts';


type TenseTabProps = {
  story: Stories.Story;
};

const getTenseChipColor = (tense: string): 'default' | 'primary' | 'secondary' | 'error' => {
  switch (tense) {
  case 'Past':
    return 'secondary';
  case 'Present':
    return 'primary';
  case 'Mixed':
    return 'error';
  default:
    return 'default';
  }
};

export const TenseTab: React.FC<TenseTabProps> = ({ story }) => {
  const analysisData = story.structuralFeedback;
  const { triggerFeedback } = React.useContext(FeedbackContext);

  const fixesContent = useMemo(() => {
    if (!analysisData) return '';
    const lines = analysisData.tenseCorrections
      .map((fix: any) => `— Change "${fix.original}" → "${fix.corrected}" (${fix.context})`)
      .join('\n');

    return `
Tense Corrections for "${story.title}"

Detected Tense: ${analysisData.tenseDominant}

Corrections:
${lines}

Backstory allowance: Some past-tense segments valid (context only)
    `.trim();
  }, [analysisData, story.title]);

  const copyAllFixes = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fixesContent);
      triggerFeedback({ severity: 'success', message: 'All tense fixes copied to clipboard.' });
    } catch {
      triggerFeedback({ severity: 'error', message: 'Unable to copy fixes.' });
    }
  }, [fixesContent, triggerFeedback]);

  if (!analysisData) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
              No structural feedback available yet.
          </Typography>
        </CardContent>
      </Card>
    );
  }


  return (
    <>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={1}>
                {analysisData.tenseDominant === 'Mixed' ? (
                  <WarningAmberRoundedIcon sx={{ color: 'warning.main' }} fontSize="small"/>
                ) : (
                  <CheckCircleIcon sx={{ color: 'success.main' }} fontSize="small"/>
                )}
                <Typography variant="subtitle1">Tense Analysis</Typography>
              </Box>
            }
          />
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography variant="body2" fontWeight={600}>
                  Detected Tense:
              </Typography>
              <Chip
                label={analysisData.tenseDominant}
                color={getTenseChipColor(analysisData.tenseDominant)}
                variant="filled"
                size="small"
              />
            </Box>
            <Box
              sx={(theme) => ({
                p: 2,
                borderRadius: 1,
                bgcolor: theme.palette.action.hover,
              })}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Backstory allowance:</strong> Some past-tense segments valid (context only)
              </Typography>
              {analysisData.tenseDominant === 'Mixed' && (
                <Typography variant="body2" sx={{ mt: 1 }} color="warning.main">
                      Mixed tense detected. Consider reviewing for consistency in key narrative moments.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardHeader
            title={<Typography variant="subtitle1">Recommended Corrections</Typography>}
            action={
              <Button variant="outlined" onClick={copyAllFixes} startIcon={<ContentCopyIcon fontSize="small"/>}>
                    Copy All Fixes
              </Button>
            }
          />
          <CardContent>
            {analysisData.tenseCorrections.length > 0 ? (
              <Stack spacing={1.5}>
                {analysisData.tenseCorrections.map((correction: any, index:any) => (
                  <Box
                    key={`${correction.original}-${index}`}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" fontWeight={600}>
                              Change:
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Box
                        component="code"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: 'error.light',
                          color: 'error.dark',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.75,
                          fontSize: '0.8125rem',
                        }}
                      >
                              "{correction.original}"
                      </Box>
                      <Typography variant="body2">→</Typography>
                      <Box
                        component="code"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: 'success.light',
                          color: 'success.dark',
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.75,
                          fontSize: '0.8125rem',
                        }}
                      >
                              "{correction.corrected}"
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Context: {correction.context}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box textAlign="center" py={6} color="text.secondary">
                <CheckCircleIcon sx={{ fontSize: 40, mb: 1 }} color="success"/>
                <Typography>No tense corrections needed</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardHeader title={<Typography variant="subtitle1">Tense Guidelines</Typography>}/>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Past Tense (Recommended for Stories)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Most effective for narrative storytelling. Creates psychological distance that helps audiences process lessons.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Present Tense
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Good for high-intensity moments or when you want immediate emotional impact.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Mixed Tense
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Generally avoid unless intentional. Can confuse audiences and weaken narrative flow.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};
